/**
 * Attention shake - g.shake(el, opts)
 *
 * Temporarily shakes an element left-right and glows it in the color of
 * opts.type (error default) to pull the eye to that part of the page, e.g.
 * g.shake(card) on a failed login. Purely visual: the classes and inline
 * CSS variables set here are removed when the effect ends, leaving the
 * element untouched. Calling it again mid-shake restarts the effect.
 *
 * Styles in scss/_shake.scss (.genus-shake*). Defaults live in
 * shake.defaults, overridable per call via opts.
 * Full option reference: docs/libs/genus.md.
 */

const TYPES = ['error', 'warning', 'info', 'success'];

const CSS_VARS = ['--genus-shake-duration', '--genus-shake-cycle', '--genus-shake-count'];

// element -> cleanup timer, so a re-shake cancels the previous cleanup
const active = new WeakMap();

export function shake(el, opts) {
	if (typeof el === 'string') el = document.querySelector(el);
	if (!(el instanceof HTMLElement)) return null;

	opts = { ...shake.defaults, ...opts };
	const type = TYPES.includes(opts.type) ? opts.type : 'error';
	const duration = opts.duration > 0 ? opts.duration : shake.defaults.duration;
	const shakes = opts.shakes > 0 ? Math.round(opts.shakes) : shake.defaults.shakes;

	stop(el);
	void el.offsetWidth; // reflow so re-adding the class restarts the animation

	el.style.setProperty('--genus-shake-duration', duration + 'ms');
	el.style.setProperty('--genus-shake-cycle', (duration / shakes) + 'ms');
	el.style.setProperty('--genus-shake-count', String(shakes));
	el.classList.add('genus-shake', 'genus-shake-' + type);

	// timer, not animationend: prefers-reduced-motion may drop the animation
	active.set(el, setTimeout(() => stop(el), duration + 50));
	return el;
}

// Global defaults. Override per call via opts, or app-wide by mutating this
// object (e.g. g.shake.defaults.type = 'warning').
shake.defaults = {
	type: 'error',  // error | warning | info | success (glow color)
	duration: 1000, // total effect length in ms
	shakes: 5,      // left-right cycles fitted into the duration
};

function stop(el) {
	const timer = active.get(el);
	if (timer !== undefined) {
		clearTimeout(timer);
		active.delete(el);
	}
	el.classList.remove('genus-shake', ...TYPES.map((t) => 'genus-shake-' + t));
	CSS_VARS.forEach((v) => el.style.removeProperty(v));
}
